import { memo, useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { IconButton } from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Channels } from '@features/tickets/models/detail-sdui/types';
import { DragNDropAndResizable, OnChangeProps } from '@shared/ui/dnd-resizable';
import { $pending, NEW_OPENED_ID } from '../../../../models/detail-sdui';
import { $openedId } from '../../../../models/detail-sdui-opened-id';
import { ChatsBlock } from './chatsBlock';
import * as Styled from './styled';

const windowPositionsName = 'ticketsWindowPositions';
const defaultPositions = {
  width: 700,
  height: 600,
  left: window.innerWidth - 700 - 10,
  top: window.innerHeight - 600 - 10,
};

export const Chats = memo(({ channels }: { channels: Channels }) => {
  const [openedId, pending] = useUnit([$openedId, $pending]);
  const [opened, setOpened] = useState(false);
  const [windowPositions, setWindowPositions] = useState(defaultPositions);

  useEffect(() => {
    const checkPositions = () => {
      if (!openedId) return;

      const positionsData = localStorage.getItem(windowPositionsName);
      const positions = positionsData ? JSON.parse(positionsData) : {};

      if (positions[openedId]) {
        setWindowPositions(positions[openedId]);

        return;
      }

      setWindowPositions(defaultPositions);
    };

    checkPositions();
  }, [openedId, opened]);

  if (!openedId || openedId === NEW_OPENED_ID) return null;

  const open = () => setOpened(true);

  const close = () => setOpened(false);

  const DndResizableContainer = ({ children }: { children?: JSX.Element }) => (
    <Styled.Container elevation={6}>{children}</Styled.Container>
  );

  const onChangeDragNDropAndResizable = (data: OnChangeProps) => {
    const positionsData = localStorage.getItem(windowPositionsName);
    const positions = positionsData ? JSON.parse(positionsData) : {};

    localStorage.setItem(
      windowPositionsName,
      JSON.stringify({ ...positions, [String(openedId)]: data })
    );
  };

  return (
    <>
      <DragNDropAndResizable
        id={`chats-${openedId}`}
        isOpened={opened}
        container={DndResizableContainer}
        onClose={close}
        headerLeft={
          <Styled.Title variant="h6">
            <IconButton
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              aria-label="Delete"
            >
              <DragIndicatorIcon />
            </IconButton>
          </Styled.Title>
        }
        body={<ChatsBlock channels={channels} pending={pending} />}
        width={windowPositions.width}
        height={windowPositions.height}
        left={windowPositions.left}
        top={windowPositions.top}
        resizePaddings={42}
        onChange={onChangeDragNDropAndResizable}
      />

      <IconButton onClick={open} disabled={opened || pending}>
        <ChatOutlinedIcon />
      </IconButton>
    </>
  );
});
